/**
 * API e stato server del pannello di amministrazione (F-063 / F-064 / F-065).
 *
 * ⚠️ I **prezzi** dei modelli sono stringhe decimali (ADR-0006): dollari per
 * token, cioè numeri dell'ordine di 1e-7. Non convertirli in `number` per
 * confrontarli o sommarli — si formattano e basta.
 *
 * I **conteggi di token** sono invece `number`: sono conteggi interi, non denaro,
 * e il backend li aggrega già come interi.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// ------------------------------------------------------------------ Utenti

export type AdminUserResources = {
  portfolios: number;
  transactions: number;
  imports: number;
  watchlistItems: number;
  wishlistItems: number;
  analyses: number;
  portfolioBriefings: number;
  marketBriefings: number;
  brokerConnections: number;
  autoTradeProposals: number;
  /** Sessioni vive: refresh token non revocati e non scaduti. */
  activeSessions: number;
};

export type AdminUserLlm = {
  windowDays: number;
  calls: number;
  totalTokens: number;
  /** Chiamate senza `usage` dal provider: il buco dichiarato, non nascosto. */
  callsWithoutUsage: number;
  lifetimeCalls: number;
  lifetimeTokens: number;
};

export type AdminUserQuota = { used: number; limit: number };

/**
 * Solo i contatori che un'azione dell'utente consuma direttamente e che ha
 * senso azzerare da qui: `briefing_request_count` (analisi dei briefing
 * automatici) resta fuori di proposito, non è qualcosa che l'utente "usa" con
 * un gesto proprio.
 */
export type AdminUserDailyQuotas = {
  /** Analisi AI richieste a mano. */
  analysis: AdminUserQuota;
  /** `null` quando la scoperta tematica è disattivata: niente da azzerare. */
  discovery: AdminUserQuota | null;
};

export type AdminUser = {
  id: string;
  email: string;
  displayName: string | null;
  baseCurrency: string;
  isPremium: boolean;
  /** Deriva da `ADMIN_EMAILS` lato server: la UI non lo deduce dall'email. */
  isAdmin: boolean;
  disabledAt: string | null;
  disabledReason: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  resources: AdminUserResources;
  llm: AdminUserLlm;
  dailyQuotas: AdminUserDailyQuotas;
};

export type AdminUsersResponse = {
  items: AdminUser[];
  total: number;
  usageDays: number;
};

export type UserStatusFilter = 'all' | 'active' | 'disabled';

export type ResetPasswordResponse = {
  /** Valorizzata solo se la password è stata generata dal server. */
  temporaryPassword: string | null;
  revokedSessions: number;
};

export const adminKeys = {
  all: ['admin'] as const,
  users: (q: string, status: UserStatusFilter) => ['admin', 'users', q, status] as const,
  llmModels: ['admin', 'llm-models'] as const,
  llmStats: (days: number) => ['admin', 'llm-stats', days] as const,
  llmIncidents: (days: number, operation: string, stage: string) =>
    ['admin', 'llm-incidents', days, operation, stage] as const,
  llmIncident: (id: string) => ['admin', 'llm-incident', id] as const,
};

export function useAdminUsers(q: string, status: UserStatusFilter) {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (status !== 'all') params.set('status', status);
  const suffix = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: adminKeys.users(q.trim(), status),
    queryFn: ({ signal }) => api.get<AdminUsersResponse>(`/admin/users${suffix}`, signal),
  });
}

export type CreateUserInput = {
  email: string;
  password?: string;
  displayName?: string;
  baseCurrency?: string;
  isPremium?: boolean;
};

export type CreateUserResponse = {
  user: AdminUser;
  /** Valorizzata solo se generata dal server: si mostra una volta sola. */
  temporaryPassword: string | null;
};

/**
 * Crea un utente dal pannello (F-082).
 *
 * Funziona **anche a registrazioni pubbliche chiuse**: `ALLOW_REGISTRATION`
 * governa chi arriva da fuori, non chi ha già il ruolo di amministratore.
 */
export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserInput) => api.post<CreateUserResponse>('/admin/users', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminKeys.all }),
  });
}

export type UpdateUserInput = {
  userId: string;
  disabled?: boolean;
  disabledReason?: string | null;
  isPremium?: boolean;
};

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: UpdateUserInput) => api.patch<AdminUser>(`/admin/users/${userId}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminKeys.all }),
  });
}

export function useResetUserPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword?: string }) =>
      api.post<ResetPasswordResponse>(
        `/admin/users/${userId}/reset-password`,
        newPassword ? { newPassword } : {},
      ),
    // Il reset revoca le sessioni: il conteggio in tabella cambia.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminKeys.all }),
  });
}

/**
 * Azzera i contatori giornalieri consumabili dall'utente (analisi AI e, se
 * attiva, scoperta tematica). Non tocca la quota dei briefing automatici, che
 * non è qualcosa che l'utente ha "speso" con un'azione propria.
 */
export function useResetDailyQuotas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<AdminUser>(`/admin/users/${userId}/reset-daily-quotas`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminKeys.all }),
  });
}

// ------------------------------------------------------------- Modelli LLM

export type LlmTier =
  | 'complex'
  | 'fast'
  | 'daily_pick'
  /** F-124: override dei modelli del grafo TradingAgents. Vuoto = nessun override. */
  | 'tradingagents_deep'
  | 'tradingagents_quick'
  /** F-131: secondo tentativo della verifica di auto-trading. Vuoto = nessun ripiego. */
  | 'autotrade_check_fallback'
  | 'agent';

export type CatalogModel = {
  modelId: string;
  name: string;
  contextLength: number | null;
  /** Dollari per token, stringa decimale (ADR-0006). */
  promptPrice: string | null;
  completionPrice: string | null;
  supportsStructuredOutput: boolean;
  /** `tools` fra i parametri supportati (F-143). */
  supportsTools?: boolean;
  isFree: boolean;
};

export type TierSetting = {
  tier: LlmTier;
  modelId: string;
  /** `environment` = nessuna riga in tabella, vale ancora la variabile d'ambiente. */
  source: 'database' | 'environment';
  environmentModelId: string;
  updatedAt: string | null;
  inCatalog: boolean;
  /** `null` quando il modello non è in catalogo: non lo sappiamo, non è "no". */
  supportsStructuredOutput: boolean | null;
  supportsTools?: boolean | null;
};

export type LlmModelsResponse = {
  tiers: TierSetting[];
  catalog: CatalogModel[];
  catalogRefreshedAt: string | null;
  llmEnabled: boolean;
};

export type CatalogRefreshResponse = {
  total: number;
  added: number;
  removed: number;
  refreshedAt: string;
};

export function useLlmModels() {
  return useQuery({
    queryKey: adminKeys.llmModels,
    queryFn: ({ signal }) => api.get<LlmModelsResponse>('/admin/llm/models', signal),
  });
}

export function useUpdateLlmModels() {
  const queryClient = useQueryClient();
  return useMutation({
    // Una mappa livello → modello, non due chiavi fisse: aggiungere un livello
    // non deve richiedere di toccare la firma (F-124).
    mutationFn: (body: Partial<Record<LlmTier, string>>) =>
      api.put<LlmModelsResponse>('/admin/llm/models', body),
    onSuccess: (data) => queryClient.setQueryData(adminKeys.llmModels, data),
  });
}

export function useRefreshLlmCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CatalogRefreshResponse>('/admin/llm/models/refresh'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: adminKeys.llmModels }),
  });
}

// ------------------------------------------------------------- Statistiche

export type UsageBucket = {
  key: string;
  calls: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  callsWithoutUsage: number;
  /**
   * Dollari, stringa decimale (ADR-0006). `null` = niente di misurabile.
   *
   * ⚠️ Somma solo le righe con un prezzo: quelle senza NON entrano come zero.
   */
  cost: string | null;
  /** Righe prezzate con lo specchio del catalogo di oggi: stime, non misure. */
  costEstimated: number;
  /** Righe senza prezzo: fuori dal totale, e dichiarate accanto. */
  costUnknown: number;
};

export type LlmStatsResponse = {
  from: string;
  to: string;
  days: number;
  totals: {
    calls: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    callsWithoutUsage: number;
    activeUsers: number;
    cost: string | null;
    costEstimated: number;
    costUnknown: number;
    /** ⚠️ Le analisi TradingAgents non passano da `llm_usage`: costo NON misurato. */
    tradingAgentsAnalyses: number;
  };
  costCurrency: 'USD';
  byOperation: UsageBucket[];
  byModel: UsageBucket[];
  byUser: Array<UsageBucket & { userId: string; email: string; displayName: string | null }>;
  byDay: Array<{ date: string; calls: number; totalTokens: number }>;
};

export function useLlmStats(days: number) {
  return useQuery({
    queryKey: adminKeys.llmStats(days),
    queryFn: ({ signal }) => api.get<LlmStatsResponse>(`/admin/stats/llm?days=${days}`, signal),
  });
}

// ── Registro degli incidenti LLM (F-164) ───────────────────────────────────

export type IncidentStage = 'transport' | 'http' | 'provider' | 'empty' | 'budget' | 'json' | 'schema' | 'semantic';

export type LlmIncidentRow = {
  id: string;
  createdAt: string;
  operation: string;
  stage: string;
  modelRequested: string | null;
  modelServed: string | null;
  message: string;
  user: { id: string; email: string; displayName: string | null } | null;
  subjectType: string | null;
  subjectId: string | null;
  promptChars: number;
  rawChars: number;
  finishReason: string | null;
  reasoningTokens: number | null;
};

export type LlmIncidentDetail = LlmIncidentRow & {
  messages: { role: string; content: string }[];
  rawResponse: string | null;
  responseMeta: Record<string, unknown> | null;
};

export type LlmIncidentsResponse = {
  items: LlmIncidentRow[];
  total: number;
  operations: string[];
};

export function useLlmIncidents(filters: { days: number; operation: string; stage: string }) {
  const params = new URLSearchParams({ days: String(filters.days) });
  if (filters.operation) params.set('operation', filters.operation);
  if (filters.stage) params.set('stage', filters.stage);
  return useQuery({
    queryKey: adminKeys.llmIncidents(filters.days, filters.operation, filters.stage),
    queryFn: ({ signal }) => api.get<LlmIncidentsResponse>(`/admin/llm/incidents?${params.toString()}`, signal),
  });
}

/** Il dettaglio si carica quando si apre la riga: prompt e risposta raw pesano. */
export function useLlmIncident(id: string) {
  return useQuery({
    queryKey: adminKeys.llmIncident(id),
    queryFn: ({ signal }) => api.get<LlmIncidentDetail>(`/admin/llm/incidents/${id}`, signal),
  });
}
